import React, { ReactNode, useCallback, useState } from 'react';
import { Box, ModalProps, Popover } from '@mui/material';
import styled from 'styled-components';
import { SPWExportButton } from 'spw-shared/globalComponents';
import { FileType } from 'spw-redux/modules/reports/types';
import { colors } from 'spw-theme';
import NameDownloadDialog from './name-download-dialog';

export const PopoverContainer = styled.div`
  width: 180px;
`;

export const DownloadNameCardContent = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: space-between;
  padding: 24px;
`;
export const DownloadOptionContainer = styled.div`
  padding: 8px 16px;
  font-size: 14px;
  line-height: 20px;
  cursor: pointer;

  &:hover {
    background-color: ${colors.whiteSmoke};
  }
`;

export type DownloadOption = {
  label: string;
  id?: string | number;
  format?: FileType;
  onClick?: () => void;
};

export type DownloadRequestOptions = {
  downloadOption?: DownloadOption;
  downloadName?: string;
};

type Props = {
  disabled: boolean;
  options?: DownloadOption[];
  shouldAskForName?: boolean;
  header?: ReactNode;
  isBusy?: boolean;
  onSubmit?: (request: DownloadRequestOptions) => void;
  initialDownloadName?: string;
  maxDownloadNameLength?: number;
  errorMessage?: string;
  popoverContainerStyle?: Record<string, string | number>;
  exportButtonClasses?: string;
};

const DownloadButtonWithOptions = ({
  disabled,
  options = [],
  shouldAskForName = true,
  header,
  onSubmit,
  isBusy,
  initialDownloadName,
  maxDownloadNameLength,
  errorMessage,
  popoverContainerStyle,
  exportButtonClasses,
}: Props) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [nameAnchorEl, setNameAnchorEl] = useState();
  const [selectedDownloadOption, setSelectedDownloadOption] = useState<
    DownloadOption
  >();

  const handleButtonClick = useCallback(
    (e: React.BaseSyntheticEvent) => {
      if (options.length) {
        setAnchorEl(e.currentTarget);
      } else {
        setNameAnchorEl(e.currentTarget);
      }
    },
    [setAnchorEl, options]
  );

  const handleOnCancel = useCallback(() => {
    setNameAnchorEl(null);
  }, [setNameAnchorEl]);

  const handlePopoverOnClose: ModalProps['onClose'] = useCallback(
    (_, reason) => {
      if (reason === 'backdropClick') {
        setAnchorEl(null);
      }
    },
    []
  );

  const handleDownloadNamePopoverOnClose: ModalProps['onClose'] = useCallback(
    (_, reason) => {
      if (reason === 'backdropClick') {
        setNameAnchorEl(null);
      }
    },
    []
  );

  const handleOnDownloadNameSubmit = useCallback(
    (name: string) => {
      setNameAnchorEl(null);
      onSubmit({
        downloadName: name,
        downloadOption: selectedDownloadOption,
      });
    },
    [onSubmit, selectedDownloadOption]
  );

  const onDownloadOptionClick = useCallback(
    (option: DownloadOption) => {
      setAnchorEl(null);
      if (option.onClick) {
        option.onClick();
      }

      if (shouldAskForName) {
        setNameAnchorEl(anchorEl);
        setSelectedDownloadOption(option);
      } else if (onSubmit) {
        onSubmit({ downloadOption: option });
      }
    },
    [anchorEl, shouldAskForName, onSubmit]
  );

  return (
    <Box width={104.5} height={38.5}>
      <SPWExportButton
        busy={isBusy}
        disabled={disabled}
        onClick={handleButtonClick}
        classes={exportButtonClasses}
      />
      {options?.length ? (
        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={handlePopoverOnClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          disableScrollLock
        >
          <PopoverContainer
            data-cy="menu-container"
            style={popoverContainerStyle}
          >
            {header}
            {options.map((option) => {
              const { label, id } = option;
              return (
                <DownloadOptionContainer
                  key={id || label}
                  onClick={() => onDownloadOptionClick(option)}
                >
                  {label}
                </DownloadOptionContainer>
              );
            })}
          </PopoverContainer>
        </Popover>
      ) : null}

      <Popover
        open={Boolean(nameAnchorEl)}
        anchorEl={nameAnchorEl}
        onClose={handleDownloadNamePopoverOnClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        disableScrollLock
      >
        <NameDownloadDialog
          onCancel={handleOnCancel}
          onSubmit={handleOnDownloadNameSubmit}
          initialName={initialDownloadName}
          maxLength={maxDownloadNameLength}
          errorMessage={errorMessage}
        />
      </Popover>
    </Box>
  );
};

export default DownloadButtonWithOptions;
